/**
 * Messages Slice
 *
 * Manages the chat messages array and the current streaming message content.
 * Provides actions for CRUD operations on messages.
 */

import type { StateCreator } from 'zustand';
import type { ChatState, MessagesSlice } from '../types';

/**
 * Initial state for the Messages slice
 */
export const messagesInitialState: Pick<
  MessagesSlice,
  'messages' | 'streamingMessage'
> = {
  messages: [],
  streamingMessage: null,
};

/**
 * Creates the messages slice with CRUD operations for chat messages.
 *
 * @example
 * // Add a message
 * useChatStore.getState().addMessage({ id: '1', text: 'Hello', author: 'user', ... });
 *
 * // Update a message
 * useChatStore.getState().updateMessage('1', { status: 'sent' });
 *
 * // Clear all messages
 * useChatStore.getState().clearMessages();
 */
export const createMessagesSlice: StateCreator<
  ChatState,
  [
    ['zustand/devtools', never],
    ['zustand/persist', unknown],
    ['zustand/immer', never],
  ],
  [],
  MessagesSlice
> = set => ({
  // Initial state
  ...messagesInitialState,

  // Actions (using Immer for cleaner mutations)
  addMessage: message => {
    set(
      state => {
        state.messages.push(message);
      },
      false,
      'messages/add'
    );
  },

  updateMessage: (id, updates) => {
    set(
      state => {
        const msg = state.messages.find(m => m.id === id);
        if (msg) Object.assign(msg, updates);
      },
      false,
      'messages/update'
    );
  },

  setMessages: messages => {
    set({ messages }, false, 'messages/set');
  },

  clearMessages: () => {
    // Preserve conversation list and user preferences when clearing messages
    // With Immer, only mutated fields are changed; others are preserved automatically
    set(
      state => {
        state.messages = [];
        state.streamingMessage = null;
        state.hasMultimodalFilesInConversation = false;
        state.isWebSearchEnabled = false;
        state.isDeepResearchEnabled = false;
        state.error = null;
      },
      false,
      'messages/clear'
    );
  },
});
